import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, ASF } from '@augusoft/asf-core';

import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AsfUser } from '../../models/asfUser';

const AuthEndpoint = 'auth';

interface AuthLoginRequest {
  u: string;
  p: string;
}

type TStorageType = 'session' | 'local';

interface AuthLoginResponse {}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loginStatus$ = new BehaviorSubject<boolean>(false);
  private user$: BehaviorSubject<AsfUser | null> = new BehaviorSubject<AsfUser | null>(null);
  private apiUrl$: string;
  private loginError$ = new BehaviorSubject<string>('');
  private appName = 'asf3';
  private storageType: TStorageType = 'local';
  private loginUrl$: string = 'login';

  setLoginUrl(url: string) {
    ASF.debug('update login url to ', url);
    this.loginUrl$ = url;
  }

  get loginUrl() {
    return this.loginUrl$;
  }

  get apiUrl(): string {
    return this.apiUrl$;
  }

  get loginError(): BehaviorSubject<string> {
    return this.loginError$;
  }
  /**
   *
   * @param apiUrl Retrieve the api Url from a provider in the project
   */
  constructor(
    @Inject('apiUrl') apiUrl: string,
    private httpClient: HttpClient,
    private api: ApiService
  ) {
    // If login from storage, check login status
    const activeUser = this.getUser();
    this.user$.next(activeUser);
    this.apiUrl$ = apiUrl;

    if (this.user$.value) {
      this.loginStatus$.next(true);
    }

  }

  public setAppName(appname: string) {
    this.appName = appname || 'asf3';

    // Reload user with correct app name
    ASF.debug('new App name', this.appName);
    const newUser = this.getUser();
    this.user$.next(newUser);
    if (this.user$.value) {
      this.loginStatus$.next(true);
    }

  }

  get token(): string | null {
    return this.user$.value?.token || null;
  }

  get loginStatus(): Observable<boolean> {
    return this.loginStatus$.asObservable();
  }

  get user(): Observable<AsfUser | null> {
    return this.user$;
  }

  public isLoggedIn(): boolean {
    return this.loginStatus$.value;
  }

  public login(username: string, password: string): Observable<boolean> {
    // Set current login error to empty string
    this.loginError$.next('');

    const requestData: AuthLoginRequest = {
      u: username,
      p: password,
    };


    // Call the authentication endpoint
    return this.api.post(AuthEndpoint, requestData)
    // return this.httpClient
    //   .post(this.apiUrl$ + AuthEndpoint, requestData)
      .pipe(catchError(this.handleLoginError.bind(this)), map(this.handleLoginResponse.bind(this)));
  }

  addHeader(name: string, value: string) {
    this.api.addHeader(name, value);
  }

  public logout(): Observable<boolean> {
    const token = this.user$.value?.token;

    sessionStorage.removeItem(this.getStorageKey());
    localStorage.removeItem(this.getStorageKey());
    this.loginStatus$.next(false);
    this.user$.next(null);
    this.storeUser();
    if (token) {
      this.api.delete(AuthEndpoint, token, 'logout').subscribe(response => {
          // no further action required
      })
    }

    return this.loginStatus;
  }

  private getStorageKey() {
    return this.appName + '-user';
  }



  private getStorage() {
    switch (this.storageType) {
        case 'session': return sessionStorage;
        case 'local': return localStorage;
    }
  }

  public setStorageType(type: 'session' | 'local' ) {
      this.storageType = type;
  }

  private getUser(): AsfUser | null {
    let storageType: TStorageType = 'session';
    let storageData = sessionStorage.getItem(this.getStorageKey());
    if(!storageData) {
      storageData = localStorage.getItem(this.getStorageKey());
      storageType = 'local';
    }
    if(!storageData) {
      return null;
    }
    this.storageType = storageType;
    const user = JSON.parse(storageData);
    if (user) {
      return user as AsfUser;
    } else {
      return null;
    }
  }

  private storeUser(): void {
    sessionStorage.removeItem(this.getStorageKey());
    localStorage.removeItem(this.getStorageKey());
    if (this.user$.value) {
      this.getStorage().setItem(this.getStorageKey(), JSON.stringify(this.user$.value));
    }
  }

  private handleLoginResponse(data: any): boolean {
    // If empty
    if (!data) {
      return false;
    }
    // Send user data to observers
    this.user$.next(data);

    // Store user object in local storage
    this.storeUser();

    // Send use status to observers
    this.loginStatus$.next(true);

    return true;
  }

  /**
   * If an error occures, check if is 401 for bad credentials
   * @param err
   */
  private handleLoginError(err: Error): Observable<boolean> {
    if (err instanceof HttpErrorResponse) {
      switch (err.status) {
        // If unauthorized error, change login status
        case 401:
          this.loginError$.next('Password or username is incorrect');
          this.loginStatus$.next(false);
          return of(false); // Login failed
      }
    }

    // Unexpected error
    return throwError(err);
  }
}
